/* eslint-disable @typescript-eslint/no-explicit-any */
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

import { type FantasyAlphaCollectionItem, type FantasyAlphaLatestResponse } from '@app/client/common/structs';

export type FantasyProfile = {
    games?: {
        player: {
            game: {
                game_code: string;
            };
            player_code: string;
            player_name: string;
        };
    }[];
    user: {
        id: number;
        created: string;
        modified: string;
        twitter_id: string;
        screen_name: string;
        name: string;
        profile_image_url: string;
        email: string;
        inviter_code: string;
        auth_type: string;
        public_address: string;
        auth_token_did: string;
        twitter_access_token: string;
        magic_issuer: string;
        user: number;
    };
};
export type FantasyPlayerInfo = {
    id: number;
    created: string;
    modified: string;
    player_name: string;
    player_code: string;
    current_list_version: number;
    current_version_editable_at: string;
    current_version_editable_until: string;
    is_spam: false;
    game: {
        id: 2;
        game_num: 2;
        max_players: 100;
        max_list_versions: 1;
        min_version_interval_hrs: 24.0;
        max_version_editable_hrs: 1.0;
        created: string;
        modified: string;
        start_at: string;
        end_at: string;
        game_code: string;
        entries_open_at: string;
    };
};

type FantasyLandingAutoGeneratedLineupValues = {
    currentGame?: FantasyAlphaLatestResponse;
    profile: FantasyProfile;
    playerInfo: FantasyPlayerInfo;
    lineupName: string;
    autoGeneratedLineup: Array<FantasyAlphaCollectionItem>;
    loadingActions: boolean;
    loadingUser: boolean;
    error: boolean;
    buttonInView: boolean;
};
interface FantasyAlphaState extends FantasyLandingAutoGeneratedLineupValues {
    setLoadingActions: (loadingActions: boolean) => void;
    setLoadingUser: (loadingUser: boolean) => void;
    setError: (error: boolean) => void;
    setProfile: (profile: FantasyProfile) => void;
    setPlayerInfo: (playerInfo: FantasyPlayerInfo) => void;
    setLineupName: (lineupName: string) => void;
    setCurrentGame: (currentGame: FantasyAlphaLatestResponse) => void;
    clearLineup: () => void;
    setAutoGeneratedLineup: (lineup: FantasyLandingAutoGeneratedLineupValues['autoGeneratedLineup']) => void;
    setButtonInView: (buttonInView: boolean) => void;
}

const initialState: FantasyLandingAutoGeneratedLineupValues = {
    error: false,
    loadingActions: false,
    loadingUser: false,
    profile: {} as FantasyProfile,
    playerInfo: {} as FantasyPlayerInfo,
    currentGame: undefined,
    autoGeneratedLineup: [],
    lineupName: '',
    buttonInView: true,
};

export const useFantasyAlphaStore = create<FantasyAlphaState>()(
    devtools(
        persist(
            (set) => ({
                ...initialState,
                setLoadingActions: (loadingActions) => set({ loadingActions }),
                setLoadingUser: (loadingUser) => set({ loadingUser }),
                setError: (error) => set({ error }),
                setCurrentGame: (currentGame) => set({ currentGame }),
                setProfile: (profile) => set({ profile }),
                setPlayerInfo: (playerInfo) => set({ playerInfo }),
                setAutoGeneratedLineup: (lineup) => set({ autoGeneratedLineup: lineup }),
                setLineupName: (lineupName) => set({ lineupName }),
                clearLineup: () =>
                    set({ autoGeneratedLineup: [], loadingActions: false, error: false, loadingUser: false }),
                setButtonInView: (buttonInView) => set({ buttonInView }),
            }),
            { name: 'fantasy-alpha-state-storage' },
        ),
    ),
);
